import React, { createContext, useContext, useState, useCallback } from "react";
import { MetaDataInterface, DatabaseType, StorageType } from "@/interfaces";
import { DEFAULT_META_DATA_CONFIG, mergeWithDefaults } from "@/generators/coreGenerator/config/DEFAULT_META_DATA_CONFIG";

// ==================== CONSTANTS ====================
// Use the centralized default configuration
export const DEFAULT_METADATA: MetaDataInterface = DEFAULT_META_DATA_CONFIG;

// ==================== TYPES ====================
interface MetaDataContextValue {
  metaData: MetaDataInterface;

  // Metadata operations
  updateMetaData: (newData: Partial<MetaDataInterface>) => void;
  setMetaData: (metaData: MetaDataInterface) => void;
  resetMetaData: () => void;

  // Specific setters
  setDatabaseType: (dbType: DatabaseType) => void;
  setStorageType: (storageType: StorageType) => void;
  toggleAuthentication: (enabled: boolean) => void;
  setAuthenticationMethods: (methods: {
    emailPassword: boolean;
    googleAuth: boolean;
  }) => void;
  toggleFileStorage: (enabled: boolean) => void;
  toggleRateLimiter: (enabled: boolean) => void;
  toggleCors: (enabled: boolean) => void;
  toggleWebsocket: (enabled: boolean) => void;
}

// ==================== CONTEXT ====================
const MetaDataContext = createContext<MetaDataContextValue | undefined>(
  undefined
);

// ==================== PROVIDER ====================
export const MetaDataContextProvider: React.FC<{
  children: React.ReactNode;
  initialMetaData?: Partial<MetaDataInterface>;
}> = ({ children, initialMetaData }) => {
  const [metaData, setMetaDataState] = useState<MetaDataInterface>(
    mergeWithDefaults(initialMetaData)
  );

  // ==================== METADATA OPERATIONS ====================
  const updateMetaData = useCallback((newData: Partial<MetaDataInterface>) => {
    setMetaDataState((prevMetaData) => ({ ...prevMetaData, ...newData }));
  }, []);

  const setMetaData = useCallback((newMetaData: MetaDataInterface) => {
    setMetaDataState(newMetaData);
  }, []);

  const resetMetaData = useCallback(() => {
    setMetaDataState(DEFAULT_META_DATA_CONFIG);
  }, []);

  // ==================== SPECIFIC SETTERS ====================
  const setDatabaseType = useCallback((dbType: DatabaseType) => {
    setMetaDataState((prev) => ({ ...prev, databaseType: dbType }));
  }, []);

  const setStorageType = useCallback((storageType: StorageType) => {
    setMetaDataState((prev) => ({ ...prev, storageType }));
  }, []);

  const toggleAuthentication = useCallback((enabled: boolean) => {
    setMetaDataState((prev) => ({ ...prev, authentication: enabled }));
  }, []);

  const setAuthenticationMethods = useCallback(
    (methods: { emailPassword: boolean; googleAuth: boolean }) => {
      setMetaDataState((prev) => ({ ...prev, authenticationMethods: methods }));
    },
    []
  );

  const toggleFileStorage = useCallback((enabled: boolean) => {
    setMetaDataState((prev) => ({ ...prev, fileStorage: enabled }));
  }, []);

  const toggleRateLimiter = useCallback((enabled: boolean) => {
    setMetaDataState((prev) => ({ ...prev, rateLimiterEnabled: enabled }));
  }, []);

  const toggleCors = useCallback((enabled: boolean) => {
    setMetaDataState((prev) => ({ ...prev, corsEnabled: enabled }));
  }, []);

  const toggleWebsocket = useCallback((enabled: boolean) => {
    setMetaDataState((prev) => ({ ...prev, websocketEnabled: enabled }));
  }, []);

  // ==================== CONTEXT VALUE ====================
  const value: MetaDataContextValue = {
    metaData,
    updateMetaData,
    setMetaData,
    resetMetaData,
    setDatabaseType,
    setStorageType,
    toggleAuthentication,
    setAuthenticationMethods,
    toggleFileStorage,
    toggleRateLimiter,
    toggleCors,
    toggleWebsocket,
  };

  return (
    <MetaDataContext.Provider value={value}>
      {children}
    </MetaDataContext.Provider>
  );
};

// ==================== HOOK ====================
export const useMetaData = () => {
  const context = useContext(MetaDataContext);
  if (!context) {
    throw new Error("useMetaData must be used within MetaDataContextProvider");
  }
  return context;
};
